import { useCallback, useEffect, useState } from "react";
import { MagnifyingGlass, UserCircle, ShieldCheck, ShieldWarning, Gavel, Key, UserPlus } from "@phosphor-icons/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LicenseUploader from "@/components/LicenseUploader";
import RegisterStaffDialog from "@/components/RegisterStaffDialog";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const ROLES = ["driver", "runner", "finance", "admin", "ceo", "coceo"];
const DISCIPLINARY_TYPES = ["Verbal Warning", "Written Warning", "Suspension", "Infraction", "Commendation", "General Note"];
const CAN_RESET_PASSWORDS = ["ceo", "coceo", "dev"];

export default function StaffPanel({ userRole = "admin" }) {
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [disc, setDisc] = useState({ action_type: "General Note", note: "" });
  const [discBusy, setDiscBusy] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [showTerminated, setShowTerminated] = useState(false);

  const refresh = useCallback(() => {
    api.get(`/staff/directory${showTerminated ? "?include_terminated=1" : ""}`).then((r) => setStaff(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [showTerminated]);

  useEffect(() => { refresh(); }, [refresh]);

  const terminateStaff = async (s) => {
    if (!window.confirm(`Terminate ${s.full_name || s.name}? The account is archived and login is blocked. All historical data is retained.`)) return;
    try {
      await api.post(`/staff/${s.id}/terminate`);
      toast.success("Staff member terminated and archived");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const recoverStaff = async (s) => {
    try {
      await api.post(`/staff/${s.id}/recover`);
      toast.success("Staff account recovered — active with all details retained");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = staff.filter((s) => {
    const q = search.toLowerCase();
    const matchQ = !q || s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q) || (s.full_name || "").toLowerCase().includes(q);
    return matchQ && (roleFilter === "all" || s.role === roleFilter);
  });

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      full_name: s.full_name || "", phone: s.phone || "", emergency_contact: s.emergency_contact || "",
      bank_name: s.bank_name || "", bank_account: s.bank_account || "", date_of_hire: s.date_of_hire || "",
      base_salary: s.base_salary || 0, role: s.role, is_active: s.is_active !== false,
    });
    setDisc({ action_type: "General Note", note: "" });
    setNewPw("");
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/staff/${editing.id}`, form);
      toast.success("Staff profile updated and saved");
      refresh();
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const licenseAction = async (approve) => {
    try {
      await api.patch(`/staff/${editing.id}`, approve ? { license_verified: true } : { license_rejected: true });
      toast.success(approve ? "License approved" : "License rejected");
      refresh();
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addDisciplinary = async () => {
    if (!disc.note.trim()) {
      toast.error("Note is required");
      return;
    }
    setDiscBusy(true);
    try {
      await api.post(`/staff/${editing.id}/disciplinary`, disc);
      toast.success("Disciplinary record added");
      setDisc({ action_type: "General Note", note: "" });
      const { data } = await api.get("/staff/directory");
      setStaff(data);
      setEditing(data.find((u) => u.id === editing.id));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDiscBusy(false);
    }
  };

  const resetPassword = async () => {
    if (newPw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setPwBusy(true);
    try {
      await api.patch(`/staff/${editing.id}/password`, { new_password: newPw });
      toast.success(`Password reset for ${editing.email}`);
      setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPwBusy(false);
    }
  };

  const lic = editing?.license || {};

  return (
    <div className="space-y-5" data-testid="staff-panel">
      <div className="flex justify-end">
        <Button data-testid="register-staff-button" onClick={() => setRegisterOpen(true)}
          className="rounded-sm bg-primary font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
          <UserPlus size={16} className="mr-2" /> Register New Staff
        </Button>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Search staff</Label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="staff-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email or legal name..." className="rounded-sm border-input bg-card pl-9 focus-visible:ring-primary" />
          </div>
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger data-testid="staff-role-filter" className="rounded-sm border-input bg-card focus:ring-primary sm:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">All roles</SelectItem>
            {ROLES.map((r) => <SelectItem key={r} value={r} className="uppercase">{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 rounded-sm border border-input bg-card px-4 py-2.5" data-testid="show-terminated-toggle">
          <Switch checked={showTerminated} onCheckedChange={(v) => setShowTerminated(!!v)} data-testid="show-terminated-switch" />
          <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Show Terminated</span>
        </label>
      </div>

      <div className="overflow-x-auto rounded-sm border border-border" data-testid="staff-table">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Staff</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Role</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Phone</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Hired</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">License</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Jobs</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Commissions</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((s) => (
              <TableRow key={s.id} data-testid={`staff-row-${s.email}`} onClick={() => openEdit(s)}
                className="cursor-pointer border-border transition-colors hover:bg-muted/50">
                <TableCell>
                  <p className="font-semibold">{s.full_name || s.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">{s.email}</p>
                </TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm font-bold uppercase tracking-wider">{s.role}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{s.phone || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{s.date_of_hire || "—"}</TableCell>
                <TableCell>
                  {s.license?.license_class ? (
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold">{s.license.license_class}</span>
                      {s.license.verified
                        ? <Badge variant="outline" className="rounded-sm border-emerald-500/40 text-[10px] font-bold uppercase text-emerald-400">Verified</Badge>
                        : s.license.rejected
                          ? <Badge variant="outline" className="rounded-sm border-red-500/40 text-[10px] font-bold uppercase text-red-400">Rejected</Badge>
                          : <Badge variant="outline" className="rounded-sm border-amber-500/40 text-[10px] font-bold uppercase text-amber-400">Pending</Badge>}
                    </span>
                  ) : <span className="text-xs text-muted-foreground">None</span>}
                </TableCell>
                <TableCell className="font-mono">{s.stats?.jobs ?? 0}</TableCell>
                <TableCell className="font-mono font-bold text-primary">RM {(s.stats?.commissions ?? 0).toFixed(2)}</TableCell>
                <TableCell>
                  {s.status === "terminated"
                    ? <Badge variant="outline" className="rounded-sm border-red-500/50 bg-red-500/10 text-[10px] font-bold uppercase text-red-400" data-testid={`terminated-badge-${s.email}`}>Terminated</Badge>
                    : s.is_active !== false
                      ? <Badge variant="outline" className="rounded-sm border-emerald-500/40 text-[10px] font-bold uppercase text-emerald-400">Active</Badge>
                      : <Badge variant="outline" className="rounded-sm border-red-500/40 text-[10px] font-bold uppercase text-red-400">Inactive</Badge>}
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">No staff found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <Dialog open onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-border bg-background" data-testid="staff-edit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 font-display">
                <UserCircle size={24} className="text-primary" /> {editing.full_name || editing.name}
                <span className="font-mono text-xs font-normal text-muted-foreground">{editing.email}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[
                ["full_name", "Full Legal Name", "text"],
                ["phone", "Phone Number", "text"],
                ["emergency_contact", "Emergency Contact", "text"],
                ["bank_name", "Bank Name", "text"],
                ["bank_account", "Bank Account Number", "text"],
                ["date_of_hire", "Date of Hire", "date"],
                ["base_salary", "Base Salary (RM / month)", "number"],
              ].map(([key, label, type]) => (
                <div key={key} className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</Label>
                  <Input data-testid={`staff-edit-${key}`} type={type} value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: type === "number" ? parseFloat(e.target.value) || 0 : e.target.value })}
                    className="rounded-sm border-input bg-card font-mono focus-visible:ring-primary" />
                </div>
              ))}
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-edit-role" className="rounded-sm border-input bg-card focus:ring-primary"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {ROLES.map((r) => <SelectItem key={r} value={r} className="uppercase">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Driver requires a verified GDL</p>
              </div>
            </div>

            <div className="mt-4 space-y-3 border-t border-border pt-5">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Driving License</Label>
              {lic.license_class && (
                <div className="flex flex-wrap items-center gap-3 rounded-sm border border-border bg-card p-4" data-testid="license-review-block">
                  <Badge variant="outline" className="rounded-sm border-primary/50 font-mono font-bold text-primary">{lic.license_class}</Badge>
                  {lic.expiry_date && <span className="font-mono text-xs text-muted-foreground">Exp: {lic.expiry_date}</span>}
                  {lic.path && (
                    <a href={fileUrl(lic.path)} target="_blank" rel="noreferrer" data-testid="license-review-link"
                      className="text-xs font-bold uppercase tracking-wider text-muted-foreground underline-offset-4 hover:text-primary hover:underline">
                      View document
                    </a>
                  )}
                  {!lic.verified && !lic.rejected && (
                    <div className="ml-auto flex gap-2">
                      <Button size="sm" data-testid="license-approve-button" onClick={() => licenseAction(true)}
                        className="rounded-sm bg-emerald-600 font-bold uppercase tracking-wider text-white hover:bg-emerald-500">
                        <ShieldCheck size={14} className="mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" data-testid="license-reject-button" onClick={() => licenseAction(false)}
                        className="rounded-sm border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                        <ShieldWarning size={14} className="mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                  {lic.verified && <Badge variant="outline" className="ml-auto rounded-sm border-emerald-500/40 text-[10px] font-bold uppercase text-emerald-400">Approved</Badge>}
                  {lic.rejected && <Badge variant="outline" className="ml-auto rounded-sm border-red-500/40 text-[10px] font-bold uppercase text-red-400">Rejected</Badge>}
                </div>
              )}
              <LicenseUploader uid={editing.id} license={editing.license}
                onDone={() => { refresh(); setEditing(null); }} />
              <label className="flex items-center justify-between rounded-sm border border-input bg-card px-4 py-3">
                <span className="text-sm font-semibold">Account active</span>
                <Switch data-testid="staff-active-switch" checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: !!v })} />
              </label>
              <p className="text-[10px] text-muted-foreground">Deactivated staff keep Payroll access for 1 month, then are fully locked out.</p>
              {editing.status === "terminated" ? (
                <div className="rounded-sm border border-red-500/40 bg-red-500/5 p-4" data-testid="terminated-block">
                  <p className="text-xs font-bold uppercase tracking-widest text-red-400">
                    Terminated · {(editing.terminated_at || "").slice(0, 10)}
                  </p>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    All historical data (invoices, payroll, logs) is retained. Recovery restores the account with personal details pre-filled.
                  </p>
                  <Button size="sm" data-testid="recover-staff-button"
                    onClick={async () => { await recoverStaff(editing); setEditing(null); }}
                    className="mt-3 rounded-sm bg-emerald-600 font-bold uppercase tracking-wider text-white hover:bg-emerald-500">
                    Recover Staff
                  </Button>
                </div>
              ) : (
                CAN_RESET_PASSWORDS.includes(userRole) && (
                  <Button size="sm" variant="outline" data-testid="terminate-staff-button"
                    onClick={async () => { await terminateStaff(editing); setEditing(null); }}
                    className="w-full rounded-sm border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                    Terminate & Archive Staff
                  </Button>
                )
              )}
            </div>

            <div className="mt-4 space-y-3 border-t border-border pt-5" data-testid="disciplinary-section">
              <Label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                <Gavel size={16} className="text-primary" /> Disciplinary Records
              </Label>
              {(editing.disciplinary || []).length > 0 ? (
                <div className="space-y-2">
                  {editing.disciplinary.map((dEntry) => (
                    <div key={dEntry.id} className="rounded-sm border border-border bg-card p-3" data-testid={`disc-entry-${dEntry.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-amber-400">{dEntry.action_type}</span>
                        <span className="font-mono text-[10px] text-muted-foreground">{dEntry.created_at?.slice(0, 10)}</span>
                      </div>
                      <p className="mt-1 text-sm">{dEntry.note}</p>
                      <p className="mt-1 text-[10px] text-muted-foreground">by {dEntry.recorded_by}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No records yet.</p>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[180px_1fr]">
                <Select value={disc.action_type} onValueChange={(v) => setDisc({ ...disc, action_type: v })}>
                  <SelectTrigger data-testid="disc-type-select" className="rounded-sm border-input bg-card focus:ring-primary"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {DISCIPLINARY_TYPES.map((dt) => <SelectItem key={dt} value={dt}>{dt}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea data-testid="disc-note-input" value={disc.note} onChange={(e) => setDisc({ ...disc, note: e.target.value })}
                  placeholder="Describe the infraction or note..." className="rounded-sm border-input bg-card focus-visible:ring-primary" rows={2} />
              </div>
              <Button size="sm" data-testid="disc-add-button" onClick={addDisciplinary} disabled={discBusy} variant="outline"
                className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
                {discBusy ? "Saving..." : "Add Record"}
              </Button>
            </div>

            {CAN_RESET_PASSWORDS.includes(userRole) && (
              <div className="mt-4 space-y-3 border-t border-border pt-5" data-testid="password-reset-section">
                <Label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  <Key size={16} className="text-primary" /> Reset Staff Password
                </Label>
                <div className="flex gap-2">
                  <Input data-testid="staff-pw-input" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                    placeholder="New password (min 6 chars)" className="rounded-sm border-input bg-card font-mono focus-visible:ring-primary" />
                  <Button data-testid="staff-pw-button" onClick={resetPassword} disabled={pwBusy} variant="outline"
                    className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
                    {pwBusy ? "..." : "Reset"}
                  </Button>
                </div>
              </div>
            )}

            <Button data-testid="staff-save-button" onClick={save} disabled={busy}
              className="mt-4 w-full rounded-sm bg-primary py-6 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
              {busy ? "Saving..." : "Save Staff Profile"}
            </Button>
          </DialogContent>
        </Dialog>
      )}
      <RegisterStaffDialog open={registerOpen} onClose={() => setRegisterOpen(false)} onDone={refresh} creatorRole={userRole} />
    </div>
  );
}
